import { useEffect, useState } from 'react'
import { AppVersion } from '../../components/AppVersion.js'
import { BrandLockup } from '../../components/BrandLockup.js'
import { Button } from '../../components/controls.js'
import { useSessionStore } from '../../state/session-store.js'
import styles from './SignInView.module.css'

/** What went wrong in a sign-in through Teamcentric Accounts, by the callback's `error`. */
const PROBLEMS: Record<string, string> = {
  expired: 'Signing in took too long. Please try again.',
  failed: 'Signing in did not work. Please try again.',
  no_account: 'Your teamcentric login has no account here yet. Ask your administrator to invite you.',
  suspended: 'This tenant is suspended. Ask your administrator.',
  blocked: 'Your access is turned off. Ask your administrator.',
}

const ACCOUNTS_START = '/api/auth/accounts/start'

/**
 * The whole application sits behind this page. Nothing is loaded until someone is signed
 * in, so a signed-out browser holds no board data of any kind.
 *
 * There is no form: people sign in at Teamcentric Accounts, so a signed-out visitor on any
 * page goes straight on, unless they came back to /login with a problem to show.
 */
export function SignInView() {
  const logOut = useSessionStore((state) => state.logOut)
  const [problem] = useState(() =>
    window.location.pathname === '/login' ? new URLSearchParams(window.location.search).get('error') : null,
  )

  // Without a click: a full page load, as the API redirects to teamcentric Accounts.
  useEffect(() => {
    if (!problem) window.location.assign(ACCOUNTS_START)
  }, [problem])

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <div className={styles.brand}>
          <img className={styles.logo} src="/logo-mark.png" alt="" width={60} height={31} />
          <div>
            <BrandLockup surface="auto" size="mobile" />
          </div>
        </div>

        <div className={styles.card}>
          <h1 className={styles.title}>Sign in</h1>
          {problem ? (
            <>
              <p className={styles.error} role="alert">
                {PROBLEMS[problem] ?? PROBLEMS.failed}
              </p>
              <button type="button" className={styles.submit} onClick={() => window.location.assign(ACCOUNTS_START)}>
                Sign in again
              </button>
              {/* Signed in at teamcentric with a login that can't get in here: signing out
                  there too lets another login be used. */}
              <Button onClick={() => void logOut()}>Sign Out</Button>
            </>
          ) : (
            <p className={styles.subtitle}>Taking you to teamcentric sign-in…</p>
          )}
        </div>
      </div>

      <p className={styles.legal}>
        <a className={`${styles.link} ${styles.legalBrand}`} href="https://teamcentric.io" target="_blank" rel="noreferrer noopener">
          <span>teamcentric.io</span>
        </a>{' '}
        &copy; 2026
        <span className={styles.dot}>&middot;</span>
        <a
          className={styles.link}
          href="https://teamcentric.io/terms"
          target="_blank"
          rel="noreferrer noopener"
        >
          Terms of use
        </a>
      </p>
      <AppVersion className={styles.version} expandable />
    </div>
  )
}
